import type { GlobalUiState } from '@protector/entities/__legacy__/redux.types';
import * as CoreActions from '@protector/entities/core/__legacy__/redux.actions';
import { CoreActionsTypes } from '@protector/entities/core/__legacy__/redux.constants';
import { selectRegion } from '@protector/entities/region/__legacy__/redux.selectors';
import { selectAllProperties } from 'entities/property/property.selectors';
import { selectSeasonEntities } from 'entities/season/season.reducer';
import type { AppState } from 'redux/app-state';
import { createSelector } from 'reselect';
import Rox from 'rox-browser';
import { selectSelectedCompany } from './core.selectors';
import { getCurrentUnitSystem } from './utils/functions';

type CoreActionsFunctions = typeof CoreActions;
type ActionsResponses = ReturnType<CoreActionsFunctions[keyof CoreActionsFunctions]>;
const getStorageItem = (key: string) =>
  typeof globalThis.localStorage?.getItem === 'function' ? globalThis.localStorage.getItem(key) : null;

export const initialState: GlobalUiState = {
  selectedCompany: null,
  selectedProperty: null,
  interfaceLanguage: (getStorageItem('currentLanguage') ?? getStorageItem('i18nextLng'))?.toLowerCase() ?? null,
  selectedSeasons: [],
  selectedRegion: null,
  selectedArea: null,
  currentUnitSystem: getCurrentUnitSystem(),
  dashboardState: {
    showCompanyWidgets: true,
    selectedProperties: [],
    selectedDateRange: undefined
  },
  layoutState: { showMonitoringDrawer: false },
  systemFlags: null
};

export default (state = initialState, action: ActionsResponses): GlobalUiState => {
  switch (action.type) {
    case CoreActionsTypes.SAVE_FLAGS:
      return {
        ...state,
        systemFlags: action.payload
      };
    case CoreActionsTypes.SELECT_COMPANY: {
      if (action.payload) Rox.setCustomStringProperty('CompanyID', action.payload);
      return {
        ...state,
        selectedCompany: action.payload
      };
    }
    case CoreActionsTypes.SELECT_PROPERTY: {
      if (action.payload) Rox.setCustomStringProperty('PropertyID', action.payload);
      return {
        ...state,
        selectedProperty: action.payload
      };
    }
    case CoreActionsTypes.SELECT_SEASONS:
      return {
        ...state,
        selectedSeasons: action.payload
      };
    case CoreActionsTypes.SELECT_REGION:
      return {
        ...state,
        selectedRegion: action.payload
      };
    case CoreActionsTypes.SELECT_AREA:
      return {
        ...state,
        selectedArea: action.payload
      };
    case CoreActionsTypes.CHANGE_UNIT_SYSTEM:
      return {
        ...state,
        currentUnitSystem: action.payload
      };
    case CoreActionsTypes.CHANGE_INTERFACE_LANGUAGE:
      return {
        ...state,
        interfaceLanguage: action.payload
      };
    case CoreActionsTypes.CHANGE_DASHBOARD_STATE:
      return {
        ...state,
        dashboardState: {
          ...state.dashboardState,
          ...action.payload
        }
      };
    case CoreActionsTypes.TOGGLE_MONITORING_DRAWER:
      return {
        ...state,
        layoutState: {
          ...state.layoutState,
          showMonitoringDrawer: !state.layoutState.showMonitoringDrawer
        }
      };
    default:
      return state;
  }
};

export const selectSelectedProperty = (state: GlobalUiState) => state.selectedProperty;

export const getCurrentRegionId = (state: AppState) => state.uiState.global.selectedRegion;

export const getCurrentRegion = createSelector(
  (state: AppState) => state,
  getCurrentRegionId,
  (state, selectedRegion) => {
    if (!selectedRegion) return null;
    return selectRegion(state)(selectedRegion);
  }
);

export const getCurrentAreaId = (state: AppState) => state.uiState.global.selectedArea;

export const getCurrentArea = createSelector(
  (state: AppState) => state,
  getCurrentAreaId,
  (state, selectedArea) => {
    if (!selectedArea) return null;
    return selectRegion(state)(selectedArea);
  }
);

export const selectSelectedSeasons = (state: GlobalUiState) => state.selectedSeasons;

export const getSelectedSeasons = (state: AppState): string[] => selectSelectedSeasons(state.uiState.global);

export const selectPropertiesFromCurrentCompany = createSelector(
  (state: AppState) => selectSelectedCompany(state.uiState.global),
  (state: AppState) => selectAllProperties(state),
  (currentCompany, allProperties) => {
    return allProperties.filter(property => {
      return property.company_id === currentCompany;
    });
  }
);

export const selectSelectedCrops = createSelector(
  (state: AppState) => selectSelectedSeasons(state.uiState.global),
  (state: AppState) => selectSeasonEntities(state.entities.season),
  (selectedSeasons, seasons) => {
    const crops = selectedSeasons
      .map(selectedSeason => {
        if (seasons[selectedSeason]) {
          return seasons[selectedSeason].crop.id;
        }
        return undefined;
      })
      .filter(c => c !== undefined);

    return crops;
  }
);

export const selectDashboardState = (state: GlobalUiState) => state.dashboardState;

export const selectSystemFlags = (state: AppState) => state.uiState.global.systemFlags;
